import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageState } from 'src/app/pages/startAPetitionV2/pageContext';
import { useRecommendationsJob } from 'src/app/pages/startAPetitionV2/shared/hooks';

import { buildSchema } from './schema';

type UseIssuePromptReturn = ModelHookResult<
	{
		schema: ReturnType<typeof buildSchema>;
		defaultValues: { issue: string };
		loading: boolean;
		error: boolean;
	},
	{
		submit: (data: { issue: string }) => void;
		back: () => void;
		persistDraft: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
		handleError: () => void;
	}
>;

export function useIssuePrompt(): UseIssuePromptReturn {
	const { actions, data } = useStartAPetitionPageState();
	const track = useTracking();
	const { translate } = useI18n();
	const { draft } = data;

	const recommendationsJobId = draft?.metadata?.recommendationsJobId;

	const { mutate, status } = useRecommendationsJob({
		jobId: recommendationsJobId,
		onSuccess: (jobResult) => {
			if (jobResult.status !== 'COMPLETED') return;

			actions.send({
				type: 'submit',
				draft: {
					input: {
						recommendations: jobResult.recommendations,
					},
					metadata: { recommendationsJobId: null },
				},
			});
		},
		onError: (error) => {
			void track('sap_recommendations_job_error', { error: error.constructor.name });
		},
	});

	const submit = useCallback(
		(values: { issue: string }) => {
			void track('sap_issue_submit', { prompt: values.issue });

			mutate(
				{ mainPrompt: values.issue, personalStory: '' },
				{
					onError: (error) => {
						void track('sap_recommendations_job_error', { error: error.constructor.name });
					},
				},
			);
		},
		[actions, track, mutate],
	);

	const back = useCallback(() => {
		void track('sap_issue_back');
		actions.send({ type: 'back', target: null });
	}, [actions, track]);

	const persistDraft = useCallback(
		(event: React.FocusEvent<HTMLTextAreaElement>) => {
			actions.send({ type: 'submit', draft: { input: { issuePrompt: event.target.value.trim() } }, target: 'self' });
		},
		[actions],
	);

	const handleError = useCallback(() => {
		actions.send({ type: 'error' });
	}, [actions]);

	return {
		data: {
			schema: buildSchema({ translate }),
			defaultValues: { issue: draft?.input?.issuePrompt ?? '' },
			loading: status === 'pending',
			error: status === 'error',
		},
		actions: { submit, back, persistDraft, handleError },
	};
}
